/**
 * AccuracyService
 * 
 * Performance diagnostic service for keyboard hardware. Handles:
 * - Polling rate estimation via inter-event interval histograms.
 * - Peak detection to identify standard reporting tiers (125Hz, 1000Hz, etc.).
 * - Chord splitting detection (identifying when simultaneous keys are reported in separate frames).
 */
export class AccuracyService extends EventTarget {
  constructor() {
    super();
    this.intervals = [];
    this.maxSamples = 500;
    this.lastEventTime = 0;
    this.currentHz = 0;
    this.chordSplitCount = 0;

    // Polling Tiers
    this.TIERS = [
      { hz: 125, interval: 8 },
      { hz: 250, interval: 4 },
      { hz: 500, interval: 2 },
      { hz: 1000, interval: 1 }
    ];
  }

  /**
   * Records a new keyboard event timestamp for polling analysis.
   * Filters out long pauses and calculates delta from the previous event.
   * 
   * @param {number} timestamp - performance.now() high-resolution timestamp.
   */
  recordEvent(timestamp) {
    if (this.lastEventTime === 0) {
      this.lastEventTime = timestamp;
      return;
    }

    const interval = timestamp - this.lastEventTime;

    // Filter out intervals from non-active typing (e.g., long pauses)
    if (interval > 0 && interval < 100) {
      this.intervals.push(interval);
      if (this.intervals.length > this.maxSamples) {
        this.intervals.shift();
      }

      this.analyzeIntervals();
    }

    this.lastEventTime = timestamp;
  }

  /**
   * Analyzes the accumulated interval buffer to estimate the polling rate (Hz).
   * Uses a histogram peak-finding strategy to determine the most frequent 
   * reporting interval.
   */
  analyzeIntervals() {
    if (this.intervals.length < 10) return;

    // Build histogram
    const histogram = {};
    this.intervals.forEach(int => {
      const rounded = Math.round(int * 10) / 10; // 0.1ms precision
      histogram[rounded] = (histogram[rounded] || 0) + 1;
    });

    // Find the most frequent interval (the peak)
    let peakInterval = 0;
    let maxCount = 0;
    Object.entries(histogram).forEach(([interval, count]) => {
      if (count > maxCount) {
        maxCount = count;
        peakInterval = parseFloat(interval);
      }
    });

    // Estimate Hz from peak
    if (peakInterval > 0) {
      this.currentHz = Math.round(1000 / peakInterval);
    }

    this.dispatchEvent(new CustomEvent('accuracyupdate', {
      detail: {
        hz: this.currentHz,
        peak: peakInterval,
        samples: this.intervals.length,
        histogram
      }
    }));
  }

  /**
   * Detects "chord splitting" — a condition where a hardware controller 
   * serializes simultaneous key presses into different USB reporting frames.
   * 
   * @param {Array<Object>} downEvents - Array of recent keydown event data.
   */
  detectChordSplit(downEvents) {
    // If multiple keys down in a very short window but not identical frame
    // Implementation would track keys pressed within 1ms but in separate events
    if (downEvents.length > 1) {
      const intervals = downEvents.slice(1).map((ev, i) => ev.timestamp - downEvents[i].timestamp);
      const isSplit = intervals.some(int => int > 0 && int < 2);
      if (isSplit) {
        this.chordSplitCount++;
        this.dispatchEvent(new CustomEvent('chordsplit', {
          detail: { count: this.chordSplitCount }
        }));
      }
    }
  }

  getHz() {
    return this.currentHz;
  }

  getSamples() {
    return this.intervals;
  }

  reset() {
    this.intervals = [];
    this.lastEventTime = 0;
    this.currentHz = 0;
    this.chordSplitCount = 0;
  }
}

export const accuracyService = new AccuracyService();
